// Divisions (contracts/phase3.md "Divisions", board E6): the tables for an ISO week,
// tier 1 first, each division ranked with its promotion (1–3, not from tier 1) and
// relegation (16–18, not from the lowest tier) zones.

import { TeamEmblem } from "@lumen/team-emblem/react";
import { useEffect } from "react";
import { Link, useLocation, useSearchParams } from "react-router";
import { useDivisions } from "../api/queries";
import type { Divisions, DivisionTeam } from "../api/schemas";
import { useCan } from "../auth/AuthContext";
import { tableStyles } from "../components/table";
import { Button, Chip, EmptyState, ErrorState, Field, Loading, PageHeader, Select } from "../components/ui";
import { fmtInt } from "../lib/format";
import { formatDateTime } from "../lib/dates";
import { divisionAnchor, ordinal, OUTCOME_LABEL, OUTCOME_TONE, WEEK_STATUS_LABEL, WEEK_STATUS_TONE, weekRange, ZONE_LABEL, ZONE_TONE } from "../lib/teams";
import styles from "./pages.module.css";
import t from "./teams.module.css";

export function DivisionsPage() {
  const [params, setParams] = useSearchParams();
  const raw = params.get("week");
  const week = raw && /^\d{4}-W\d{2}$/.test(raw) ? raw : null;
  const q = useDivisions(week);
  const canRun = useCan("jobs.run");
  const location = useLocation();
  const data = q.data;
  const weeks = data?.weeks ?? [];
  const at = data ? weeks.findIndex((w) => w.week === data.week) : -1;
  const go = (w: string | null) => setParams(w ? { week: w } : {}, { replace: true });

  // Jump to a division table (links from team detail carry #d<tier>-<index>).
  useEffect(() => {
    if (!data || !location.hash) return;
    document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: "start" });
  }, [data, location.hash]);

  const teams = data ? data.tiers.reduce((n, tier) => n + tier.divisions.reduce((m, d) => m + d.teams.length, 0), 0) : 0;
  return (
    <>
      <PageHeader
        title="Divisions"
        meta={<span>Tier 1 is the top. Each division holds 18 teams; ranks 1–3 go up and 16–18 go down when the week closes (Monday 00:05 UTC).</span>}
      />
      <div className={styles.filters}>
        <Button size="sm" variant="ghost" aria-label="Previous week" disabled={at < 0 || at >= weeks.length - 1} onClick={() => go(weeks[at + 1]!.week)}>
          ←
        </Button>
        <Field label="Week">
          <Select value={data?.week ?? week ?? ""} onChange={(e) => go(e.target.value || null)} aria-label="Week" style={{ width: 320 }}>
            {weeks.length === 0 && data ? <option value={data.week}>{data.week}</option> : null}
            {weeks.map((w) => (
              <option key={w.week} value={w.week}>
                {`${w.week} · ${weekRange(w.week)}${w.status ? ` · ${WEEK_STATUS_LABEL[w.status]}` : ""}`}
              </option>
            ))}
          </Select>
        </Field>
        <Button size="sm" variant="ghost" aria-label="Next week" disabled={at <= 0} onClick={() => go(at === 1 ? null : weeks[at - 1]!.week)}>
          →
        </Button>
        <span className={styles.filtersEnd}>
          {data?.status ? <Chip tone={WEEK_STATUS_TONE[data.status]}>{WEEK_STATUS_LABEL[data.status]}</Chip> : null}
          {data ? <span className="monoLabel" data-testid="divisions-meta">{`${data.tiers.length} tier${data.tiers.length === 1 ? "" : "s"} · ${teams} team${teams === 1 ? "" : "s"}`}</span> : null}
          <span className={t.zoneKey} aria-label="Zone key">
            <span data-zone="promotion">Promotion</span>
            <span data-zone="relegation">Relegation</span>
          </span>
        </span>
      </div>

      {data?.status === "pending" ? (
        <p className={t.banner} data-testid="pending-banner">
          {data.week} has ended but isn't closed yet: its promotions and relegations haven't been applied.{" "}
          {canRun ? <Link to={`/jobs?week=${data.week}`}>Preview the close in Jobs →</Link> : "An admin can run the close from Jobs."}
        </p>
      ) : data?.status === "open" ? (
        <p className={t.bannerQuiet}>Standings so far this week. Scores move until Sunday 23:59 UTC.</p>
      ) : data?.status === "closed" ? (
        <p className={t.bannerQuiet} data-testid="closed-banner">
          The snapshot taken when {data.week} closed{data.closedAt ? ` (${formatDateTime(data.closedAt)})` : ""}, with each team's outcome.
        </p>
      ) : null}

      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : q.isPending ? (
        <Loading rows={12} />
      ) : data!.tiers.length === 0 ? (
        <EmptyState title="No divisions">No team played in {data!.week}.</EmptyState>
      ) : (
        <div className={t.tiers} aria-busy={q.isFetching} data-testid="divisions">
          {data!.tiers.map((tier) => (
            <section key={tier.tier} className={t.tier} aria-labelledby={`tier-${tier.tier}`}>
              <h2 id={`tier-${tier.tier}`} className={t.tierTitle}>
                <span className="monoLabel">Tier {tier.tier}</span>
                <span>{tier.tier === 1 ? "Top tier" : tier.tier === data!.lowestTier ? "Lowest tier" : `Tier ${tier.tier}`}</span>
                <span className={styles.faint}>
                  {tier.divisions.length} division{tier.divisions.length === 1 ? "" : "s"}
                </span>
              </h2>
              <div className={t.divisionGrid}>
                {tier.divisions.map((d) => (
                  <DivisionTable key={d.index} tier={tier.tier} index={d.index} teams={d.teams} data={data!} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </>
  );
}

function DivisionTable({ tier, index, teams, data }: { tier: number; index: number; teams: DivisionTeam[]; data: Divisions }) {
  return (
    <div className={t.division} id={divisionAnchor(tier, index)} data-testid={`division-${tier}-${index}`}>
      <header className={t.divisionHead}>
        <span className={t.divisionName}>
          Division {tier} · {index}
        </span>
        <span className="monoLabel">{teams.length} / 18</span>
      </header>
      <div className={tableStyles.wrap}>
        <table className={`${tableStyles.table} ${t.standings}`}>
          <thead>
            <tr>
              <th data-align="right">#</th>
              <th>Team</th>
              <th data-align="right">Score</th>
              <th data-align="right">Sets</th>
              <th data-align="right">Members</th>
              <th>{data.status === "closed" ? "Outcome" : "Zone"}</th>
            </tr>
          </thead>
          <tbody>
            {teams.map((row) => (
              <tr key={row.teamId} data-zone={row.zone ?? "safe"} data-team={row.teamId}>
                <td data-align="right" className="mono">
                  {String(row.rank).padStart(2, "0")}
                </td>
                <td>
                  <Link to={`/teams/${row.teamId}`} className={t.teamCell}>
                    {row.emblem ? <TeamEmblem emblem={row.emblem} size={18} /> : null}
                    <span className={t.teamName}>{row.name}</span>
                  </Link>
                </td>
                <td data-align="right" className="mono">
                  {fmtInt(row.score)}
                </td>
                <td data-align="right" className="mono">
                  {row.sets ?? "—"}
                </td>
                <td data-align="right" className="mono">
                  {row.members ?? "—"}
                </td>
                <td>
                  {row.outcome && row.outcome !== "stayed" ? (
                    <span className={t.outcome}>
                      <Chip tone={OUTCOME_TONE[row.outcome]}>{OUTCOME_LABEL[row.outcome]}</Chip>
                      {row.newTier ? <span className="mono">→ D{row.newTier}</span> : null}
                    </span>
                  ) : row.zone && row.zone !== "safe" && !row.outcome ? (
                    <Chip tone={ZONE_TONE[row.zone]}>{ZONE_LABEL[row.zone]}</Chip>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.status === "closed" ? null : (
        <p className={t.divisionFoot}>
          {teams.length >= 3 && tier > 1 ? `${teams.slice(0, 3).map((x) => x.name).join(", ")} would go up` : "Nobody goes up from here"}
          {tier < data.lowestTier && teams.length >= 16 ? ` · ${ordinal(16)}–${ordinal(teams.length)} would go down` : ""}.
        </p>
      )}
    </div>
  );
}
